import { useState } from 'react';
import { FiSend, FiCheckCircle, FiSearch, FiClock, FiAlertCircle, FiHash } from 'react-icons/fi';
import { submitSupport, checkSupportTicket } from '../../services/api';
import styles from './LandingLayout.module.css';

const SUBJECTS = [
  'General Question',
  'Technical Issue',
  'Account / Login Problem',
  'Billing / Subscription',
  'Donation / Payment Issue',
  'Feature Request',
  'Derasar Registration Help',
  'Other',
];

const STATUS_CONFIG = {
  open:        { label: 'Open',        color: '#2563eb', bg: '#eff6ff' },
  in_progress: { label: 'In Progress', color: '#d97706', bg: '#fffbeb' },
  resolved:    { label: 'Resolved',    color: '#16a34a', bg: '#f0fdf4' },
};

const emptyForm = { name: '', email: '', subject: '', message: '' };

const SupportPage = () => {
  const [tab, setTab] = useState('submit'); // 'submit' | 'check'

  // Submit form state
  const [form, setForm]             = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(null); // { ticketNo, createdAt }
  const [submitError, setSubmitError] = useState('');

  // Check status state
  const [ticketInput, setTicketInput] = useState('');
  const [ticketEmail, setTicketEmail] = useState('');
  const [checking, setChecking]       = useState(false);
  const [ticketResult, setTicketResult] = useState(null);
  const [checkError, setCheckError]   = useState('');

  // -- Submit form -------------------------------------------------------------
  const validate = () => {
    const e = {};
    if (!form.name.trim())                      e.name    = 'Your name is required';
    if (!form.email.trim())                     e.email   = 'Your email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email   = 'Enter a valid email address';
    if (!form.subject)                          e.subject = 'Please select a subject';
    if (form.message.trim().length < 10)        e.message = 'Message must be at least 10 characters';
    return e;
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (formErrors[e.target.name]) setFormErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await submitSupport(form);
      setSubmitted(res.data);
      setForm(emptyForm);
    } catch {
      setSubmitError('Something went wrong. Please try again or email us at support@derasarfunding.in');
    } finally {
      setSubmitting(false);
    }
  };

  // -- Check status ------------------------------------------------------------
  const handleCheck = async (e) => {
    e.preventDefault();
    if (!ticketInput.trim() || !ticketEmail.trim()) { setCheckError('Please enter your ticket number and email'); return; }
    setChecking(true);
    setCheckError('');
    setTicketResult(null);
    try {
      const res = await checkSupportTicket(ticketInput.trim(), ticketEmail.trim());
      setTicketResult(res.data);
    } catch (err) {
      setCheckError(err.response?.status === 404
        ? 'No ticket found with that number. Please check and try again.'
        : 'Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <div className={styles.sectionLabel}>Support</div>
        <h2 className={styles.sectionTitle}>Help &amp; Support</h2>
        <p className={styles.sectionLead}>
          Raise a support request or check the status of an existing ticket.
          Our team responds within one business day.
        </p>

        {/* -- Tabs -- */}
        <div className={styles.supportTabs}>
          <button
            className={`${styles.supportTab} ${tab === 'submit' ? styles.supportTabActive : ''}`}
            onClick={() => setTab('submit')}
          >
            <FiSend size={14} /> Raise a Request
          </button>
          <button
            className={`${styles.supportTab} ${tab === 'check' ? styles.supportTabActive : ''}`}
            onClick={() => setTab('check')}
          >
            <FiSearch size={14} /> Check Ticket Status
          </button>
        </div>

        {/* -- Submit tab -- */}
        {tab === 'submit' && (
          submitted ? (
            <div className={styles.ticketSuccess}>
              <div className={styles.ticketSuccessIcon}><FiCheckCircle size={32} /></div>
              <h3>Request Submitted</h3>
              <p>Your support request has been received. Here is your ticket number:</p>
              <div className={styles.ticketNumber}>
                <FiHash size={16} />{submitted.ticketNo}
              </div>
              <p className={styles.ticketSuccessNote}>
                Save this number to track your request. We will respond to your email within one business day.
              </p>
              <button
                className={styles.btnCheckStatus}
                onClick={() => { setTicketInput(submitted.ticketNo); setTab('check'); setTicketResult(null); }}
              >
                <FiSearch size={14} /> Check ticket status
              </button>
            </div>
          ) : (
            <form className={styles.supportForm} onSubmit={handleSubmit} noValidate>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="name">Your Name</label>
                  <input id="name" name="name" className={styles.formInput} value={form.name} onChange={handleChange} placeholder="Raj Mehta" autoComplete="name" />
                  {formErrors.name && <span className={styles.formError}>{formErrors.name}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="email">Your Email</label>
                  <input id="email" name="email" type="email" className={styles.formInput} value={form.email} onChange={handleChange} placeholder="raj@example.com" autoComplete="email" />
                  {formErrors.email && <span className={styles.formError}>{formErrors.email}</span>}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="subject">Subject</label>
                <select id="subject" name="subject" className={styles.formInput} value={form.subject} onChange={handleChange}>
                  <option value="">Select a subject…</option>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {formErrors.subject && <span className={styles.formError}>{formErrors.subject}</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="message">Message</label>
                <textarea id="message" name="message" className={styles.formTextarea} value={form.message} onChange={handleChange} placeholder="Describe your question or issue in detail…" />
                {formErrors.message && <span className={styles.formError}>{formErrors.message}</span>}
              </div>

              {submitError && <p className={styles.formError}>{submitError}</p>}

              <button type="submit" className={styles.btnSubmit} disabled={submitting}>
                <FiSend size={15} />
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </form>
          )
        )}

        {/* -- Check status tab -- */}
        {tab === 'check' && (
          <div className={styles.checkSection}>
            <form className={styles.checkForm} onSubmit={handleCheck}>
              <input
                className={styles.formInput}
                type="email"
                value={ticketEmail}
                onChange={e => { setTicketEmail(e.target.value); setCheckError(''); setTicketResult(null); }}
                placeholder="Email used when raising the ticket"
              />
              <div className={styles.checkInputRow}>
                <input
                  className={styles.formInput}
                  value={ticketInput}
                  onChange={e => { setTicketInput(e.target.value.toUpperCase()); setCheckError(''); setTicketResult(null); }}
                  placeholder="e.g. TKT-20250423-0001"
                  style={{ flex: 1, fontFamily: 'monospace', letterSpacing: '0.04em' }}
                />
                <button type="submit" className={styles.btnSubmit} style={{ flexShrink: 0 }} disabled={checking}>
                  <FiSearch size={15} /> {checking ? 'Checking…' : 'Check'}
                </button>
              </div>
              {checkError && <p className={styles.formError} style={{ marginTop: 8 }}>{checkError}</p>}
            </form>

            {ticketResult && (() => {
              const cfg = STATUS_CONFIG[ticketResult.status] || STATUS_CONFIG.open;
              return (
                <div className={styles.ticketCard}>
                  <div className={styles.ticketCardHeader}>
                    <div>
                      <div className={styles.ticketCardNo}><FiHash size={13} />{ticketResult.ticketNo}</div>
                      <div className={styles.ticketCardSubject}>{ticketResult.subject}</div>
                    </div>
                    <span className={styles.statusBadge} style={{ background: cfg.bg, color: cfg.color }}>
                      {ticketResult.status === 'open' && <FiAlertCircle size={12} />}
                      {ticketResult.status === 'in_progress' && <FiClock size={12} />}
                      {ticketResult.status === 'resolved' && <FiCheckCircle size={12} />}
                      {cfg.label}
                    </span>
                  </div>
                  <div className={styles.ticketCardMeta}>
                    <span>Submitted: {fmt(ticketResult.createdAt)}</span>
                    {ticketResult.resolvedAt && <span>Resolved: {fmt(ticketResult.resolvedAt)}</span>}
                  </div>
                  {ticketResult.resolution && (
                    <div className={styles.ticketResolution}>
                      <div className={styles.ticketResolutionLabel}>Resolution from our team</div>
                      <p>{ticketResult.resolution}</p>
                    </div>
                  )}
                  {ticketResult.status !== 'resolved' && (
                    <p className={styles.ticketPending}>
                      <FiClock size={13} /> Our team is working on your request. We will respond within one business day.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </section>
  );
};

export default SupportPage;
